import {Box, Paper, Typography} from "@mui/material";
import {OutlinedDropdownForm} from "../../../../../../components/base/dropdown/outlined_dropdown";
import {OutlinedInputForm} from "../../../../../../components/base/input/outlined_input";
import SaveOrRollback from "../../../../../../components/app-specific/save-or-rollback";
import {deepEqual} from "../../../../../../../core/services/utils/utils";
import React, {useCallback} from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";


const PaymentSettingsSection = ({customer, setCustomer, values, initialValues, form, formData, required, deleting, submitting, refetchAccounting}) => {
    const isMounted = useIsMounted();

    /**
     * Updates the payment terms of the company by calling the api from the server, and if the result is successful,
     * will apply the changes in the state.
     * @type {(function(*=, *=): void)}
     */
    const updatePaymentTerms = useCallback(async (newPaymentTerm, setLoading) => {
        setLoading(true);
        const forApi = {
            id: customer?.id,
            paymentTerms: [newPaymentTerm],
        }
        const response = await Api.updateCustomerPaymentTerms(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            void refetchAccounting();
            setCustomer(prevState => ({...prevState, paymentTerms: newPaymentTerm}))
        }
        setLoading(false)
    }, [customer?.id, isMounted, setCustomer, refetchAccounting])

    /**
     * Calls the api for updating the company's credit limit and if successful, then
     *
     * @type {(function(*=, *=): void)}
     */
    const updateCreditLimit = useCallback(async (newCreditLimit, setLoading) => {
        setLoading(true);
        const response = await Api.updateCustomerInfo({
            id: customer.id,
            creditLimit: newCreditLimit,
            name: customer?.name,
            country: formData.countries?.find(e => e.id === customer?.countryId),
            province: formData.provinces?.find(e => e.id === customer?.provinceId),
            city: customer?.city,
            phone: customer?.phone,
            postalCode: customer?.postalCode,
            address: customer?.address,
        });
        if (!isMounted()) return;
        if (response?.resultFlag) {
            void refetchAccounting();
            setCustomer(prevState => ({...prevState, creditLimit: newCreditLimit}))
        }
        setLoading(false);
    }, [customer?.address, customer?.city, customer?.countryId, customer?.id, customer?.name, customer?.phone, customer?.postalCode, customer?.provinceId, formData.countries, formData.provinces, isMounted, setCustomer, refetchAccounting])


    return (
        <>
            <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%', gap: 3}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Payment Settings
                    </Typography>
                </Box>
                <Grid2 container spacing={2}>
                    <Grid2 xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                        <OutlinedDropdownForm
                            label={'Payment Terms'}
                            name={`paymentTerms`}
                            required={required.paymentTerms}
                            renderValue={(value) => value ? `${value.description} (${value.code})` : ''}
                            data={formData.paymentTerms?.map(e => ({
                                code: e.code,
                                description: e.description,
                                id: e.id,
                            }))?.map(e => ({
                                value: e,
                                label: `${e.description} (${e.code})`,
                                id: e.code,
                            })) ?? []}
                        />
                        <SaveOrRollback
                            column={false}
                            show={!deepEqual(values.paymentTerms, initialValues.paymentTerms) && !deleting}
                            onRollback={() => form.change('paymentTerms', initialValues.paymentTerms)}
                            onSave={(setLoading) => !submitting && updatePaymentTerms(values.paymentTerms, setLoading)}
                        />
                    </Grid2>
                    <Grid2 xs={12} sx={{display: 'flex', alignItems: 'center'}}>
                        <OutlinedInputForm
                            label={'Credit Limit'}
                            name={'creditLimit'}
                            type={'number'}
                            startAdornment={'$'}
                            required={required.creditLimit}
                            value={values?.creditLimit?.toString() ?? ''}
                            onChange={(e) => form.change(e.target.name, e.target.value)}
                            onBlur={(e) => {
                                if (!e.target.value?.trim()) {
                                    form.change(e.target.name, 0)
                                } else if (Number.isNaN(Number(e.target.value))) {
                                    form.change(e.target.name, 0)
                                } else {
                                    form.change(e.target.name, Math.max(0, Number(e.target.value)))
                                }
                            }}
                            InputProps={{
                                inputProps: {min: 0}
                            }}
                        />
                        <SaveOrRollback
                            column={false}
                            show={!deepEqual(values.creditLimit, initialValues.creditLimit) && !!values.creditLimit && !deleting}
                            onRollback={() => form.change('creditLimit', initialValues.creditLimit)}
                            onSave={(setLoading) => !submitting && updateCreditLimit(parseInt(values.creditLimit), setLoading)}
                        />
                    </Grid2>
                </Grid2>
            </Paper>
        </>
    );
}

export default PaymentSettingsSection;